import { applyVenueLayout } from './layout-service'
import { buildAssistSnapshot } from '../assist/snapshot'
import type { AssistAllocation } from '../assist/snapshot'
import type { AssistConfiguration } from '../assist/types'
import { saveCachedAssistConfiguration } from '../../lib/offlineStore'
import type { VenueTableLayout } from './types'
import { UserFacingError } from '../../utils/UserFacingError.ts'
import { supabase } from '../../lib/supabase'
import { quantityAmountCents } from '../../lib/format'
import { splitLegacyMixerModifiers } from '../../lib/mixers'
import { normalizeCatalogSnapshot } from '../catalog/services/catalogSnapshots'
import type { AppliedDiscount, Customer, PaymentMethod, SaleLineCatalogSnapshot, TenantContext, TicketLineComponent, TicketLineMixer, TicketLineModifier } from '../../types/domain'
import type { CloseRestaurantOrderResult, DiningArea, DiningAreaCreateInput, DiningAreaUpdateInput, MoveRestaurantOrderLinesResult, OpenRestaurantOrderInput, PayRestaurantEqualPartResult, PayRestaurantOrderItemsResult, QuickSaleExistingTableSaveInput, QuickSaleExistingTableSaveResult, QuickSaleVirtualTableCreateInput, QuickSaleVirtualTableCreateResult, RestaurantEqualSplit, RestaurantMap, RestaurantOrder, RestaurantOrderDetail, RestaurantOrderGroupDetail, RestaurantOrderLine, RestaurantOrderLineMove, RestaurantTable, RestaurantTableCreateInput, RestaurantTableMapItem, RestaurantTableReservation, RestaurantTableUpdateInput, SaveRestaurantOrderLinesResult, VirtualRestaurantTableCreateInput, VirtualRestaurantTableDeleteInput } from './types'
import { getOrderPendingUnits } from './service-status'
import { buildCatalogOrderLinesPayload, buildRestaurantOrderLinesPayload } from './order-line-payload'
import { normalizeMapElements } from './map-elements'
import { getDateRange, localDateKey } from '../reservations/domain/reservationAvailability'
import { hasTenantCapability } from '../platform/tenantFeatureAccess'
import type { CashlogyTransaction } from '../local-printing/types'

export { buildRestaurantOrderLinesPayload } from './order-line-payload'

type AreaRow = { id: string; tenant_id: string; venue_id: string; name: string; sort_order: number; is_active: boolean; canvas_width: number; canvas_height: number; map_elements: unknown; created_at: string; updated_at: string }
type TableRow = { id: string; tenant_id: string; venue_id: string; area_id: string | null; cash_session_id: string | null; name: string; capacity: number; shape: RestaurantTable['shape']; position_x: number; position_y: number; width: number; height: number; is_active: boolean; sort_order: number; reserved_until: string | null; reservation_note: string | null; created_at: string; updated_at: string }
type OrderRow = { id: string; tenant_id: string; venue_id: string; cash_session_id: string; cash_register_id: string; opened_by_user_id: string; opened_by_device_id: string; guest_count: number; status: RestaurantOrder['status']; revision: number; order_group_id: string; split_sequence: number; draft_discount: AppliedDiscount | null; opened_at: string; updated_at: string; closed_at: string | null }
type OrderTableRow = { order_id: string; order_group_id: string; table_id: string; joined_at: string; released_at: string | null }
type ReservationMapValue = { id: string; customer_name: string; customer_phone: string; party_size: number; starts_at: string; ends_at: string; status: RestaurantTableReservation['status'] }
type ReservationMapRow = { table_id: string; reservations: ReservationMapValue | ReservationMapValue[] }
type OrderLineRow = { id: string; tenant_id: string; venue_id: string; order_id: string; product_id: string | null; variant_id: string | null; product_name: string; variant_name: string; unit_price_cents: number; quantity: number; served_quantity: number; fully_served_at: string | null; modifiers: TicketLineModifier[]; components: TicketLineComponent[] | null; catalog_snapshot: Partial<SaleLineCatalogSnapshot> | null; mixer_product_id: string | null; mixer: TicketLineMixer | null; note: string | null; created_at: string; updated_at: string }

const areaColumns = 'id, tenant_id, venue_id, name, sort_order, is_active, canvas_width, canvas_height, map_elements, created_at, updated_at'
const tableColumns = 'id, tenant_id, venue_id, area_id, cash_session_id, name, capacity, shape, position_x, position_y, width, height, is_active, sort_order, reserved_until, reservation_note, created_at, updated_at'
const orderColumns = 'id, tenant_id, venue_id, cash_session_id, cash_register_id, opened_by_user_id, opened_by_device_id, guest_count, status, revision, order_group_id, split_sequence, draft_discount, opened_at, updated_at, closed_at'
const lineColumns = 'id, tenant_id, venue_id, order_id, product_id, variant_id, product_name, variant_name, unit_price_cents, quantity, served_quantity, fully_served_at, modifiers, components, catalog_snapshot, mixer_product_id, mixer, note, created_at, updated_at'

function requireSupabase() {
  if (!supabase) throw new Error('Supabase no está configurado.')
  return supabase
}

const mapArea = (row: AreaRow): DiningArea => ({ id: row.id, tenantId: row.tenant_id, venueId: row.venue_id, name: row.name, sortOrder: row.sort_order, isActive: row.is_active, canvasWidth: row.canvas_width, canvasHeight: row.canvas_height, mapElements: normalizeMapElements(row.map_elements), createdAt: row.created_at, updatedAt: row.updated_at })
const mapTable = (row: TableRow): RestaurantTable => ({ id: row.id, tenantId: row.tenant_id, venueId: row.venue_id, areaId: row.area_id ?? `virtual:${row.venue_id}`, cashSessionId: row.cash_session_id, isVirtual: row.cash_session_id !== null, name: row.name, capacity: row.capacity, shape: row.shape, positionX: Number(row.position_x), positionY: Number(row.position_y), width: Number(row.width), height: Number(row.height), isActive: row.is_active, sortOrder: row.sort_order, reservedUntil: row.reserved_until, reservationNote: row.reservation_note, createdAt: row.created_at, updatedAt: row.updated_at })
const mapOrder = (row: OrderRow): RestaurantOrder => ({ id: row.id, tenantId: row.tenant_id, venueId: row.venue_id, cashSessionId: row.cash_session_id, cashRegisterId: row.cash_register_id, openedByUserId: row.opened_by_user_id, openedByDeviceId: row.opened_by_device_id, guestCount: row.guest_count, status: row.status, revision: row.revision, orderGroupId: row.order_group_id, splitSequence: row.split_sequence, draftDiscount: row.draft_discount, openedAt: row.opened_at, updatedAt: row.updated_at, closedAt: row.closed_at })
const mapLine = (row: OrderLineRow): RestaurantOrderLine => {
  const selection = splitLegacyMixerModifiers(row.modifiers, row.mixer_product_id, row.mixer)
  return { id: row.id, tenantId: row.tenant_id, venueId: row.venue_id, orderId: row.order_id, productId: row.product_id, variantId: row.variant_id, productName: row.product_name, variantName: row.variant_name, unitPriceCents: row.unit_price_cents, quantity: row.quantity, servedQuantity: Number(row.served_quantity), fullyServedAt: row.fully_served_at, modifiers: selection.modifiers, components: row.components?.length ? row.components : selection.components, catalogSnapshot: normalizeCatalogSnapshot(row.catalog_snapshot, { productId: row.product_id, productName: row.product_name, variantId: row.variant_id, variantName: row.variant_name, basePriceCents: row.unit_price_cents }), mixerProductId: selection.mixerProductId, mixer: selection.mixer, note: row.note, createdAt: row.created_at, updatedAt: row.updated_at }
}

const venueConfig = new Map<string, { enabled: boolean; expiresAt: number }>()

export async function loadVenueTablesEnabled(context: TenantContext, venueId = context.venueId) {
  const key = context.tenantId + ':' + venueId + ':' + context.userId
  const cached = venueConfig.get(key)
  if (cached && cached.expiresAt > Date.now()) return cached.enabled
  const { data, error } = await requireSupabase().from('venues').select('tables_enabled').eq('tenant_id', context.tenantId).eq('id', venueId).single<{ tables_enabled: boolean }>()
  if (error) throw error
  venueConfig.set(key, { enabled: data.tables_enabled, expiresAt: Date.now() + 60_000 })
  return data.tables_enabled
}

export async function setVenueTablesEnabled(venueId: string, enabled: boolean) {
  const { data, error } = await requireSupabase().rpc('set_venue_tables_enabled', { p_venue_id: venueId, p_enabled: enabled })
  if (error) throw error
  venueConfig.clear()
  return Boolean(data)
}

export async function loadDiningAreas(context: TenantContext, venueId = context.venueId, includeInactive = false): Promise<DiningArea[]> {
  let query = requireSupabase().from('dining_areas').select(areaColumns).eq('tenant_id', context.tenantId).eq('venue_id', venueId).order('sort_order')
  if (!includeInactive) query = query.eq('is_active', true)
  const { data, error } = await query
  if (error) throw error
  return ((data ?? []) as AreaRow[]).map(mapArea)
}

export async function loadRestaurantTables(context: TenantContext, venueId = context.venueId, includeInactive = false): Promise<RestaurantTable[]> {
  let query = requireSupabase().from('restaurant_tables').select(tableColumns).eq('tenant_id', context.tenantId).eq('venue_id', venueId).is('cash_session_id', null).order('sort_order')
  if (!includeInactive) query = query.eq('is_active', true)
  const { data, error } = await query
  if (error) throw error
  return ((data ?? []) as TableRow[]).map(mapTable)
}

export async function loadRestaurantMap(context: TenantContext, cashSessionId?: string): Promise<RestaurantMap> {
  const client = requireSupabase()
  const response = await client.rpc('pos_restaurant_map', { p_tenant_id: context.tenantId, p_venue_id: context.venueId, p_cash_session_id: cashSessionId ?? null, p_include_production: hasTenantCapability(context, 'production') })
  if (response.error && !['PGRST202','42883'].includes(response.error.code)) throw response.error
  const snapshot = response.error ? null : response.data as { areas: AreaRow[]; tables: TableRow[]; orders: OrderRow[]; lines: OrderLineRow[]; links: OrderTableRow[]; splits: Array<{ order_group_id: string; paid_cents: number }>; reservations: ReservationMapRow[]; allocations: Array<{ current_order_line_id: string; ready_quantity: number }>; layout: VenueTableLayout | null }
  let loadedAreas: DiningArea[] = snapshot ? snapshot.areas.map(mapArea) : []
  let tablesResult = { data: (snapshot?.tables ?? []) as NonNullable<typeof snapshot>['tables'] | null, error: null as unknown }
  let linksResult = { data: (snapshot?.links ?? []) as NonNullable<typeof snapshot>['links'] | null, error: null as unknown }
  let ordersResult = { data: (snapshot?.orders ?? []) as NonNullable<typeof snapshot>['orders'] | null, error: null as unknown }
  let equalSplitsResult = { data: (snapshot?.splits ?? []) as NonNullable<typeof snapshot>['splits'] | null, error: null as unknown }
  let reservationsResult = { data: (snapshot?.reservations ?? []) as NonNullable<typeof snapshot>['reservations'] | null, error: null as unknown }
  if (!snapshot) {
    const { data: venue, error: venueError } = await client.from('venues').select('timezone').eq('tenant_id', context.tenantId).eq('id', context.venueId).single<{ timezone: string }>()
    if (venueError) throw venueError
    const reservationRange = getDateRange(localDateKey(new Date(), venue.timezone), venue.timezone)
    const tablesQuery = client.from('restaurant_tables').select(tableColumns).eq('tenant_id', context.tenantId).eq('venue_id', context.venueId).eq('is_active', true).order('sort_order')
    ;[loadedAreas, tablesResult, linksResult, ordersResult, equalSplitsResult, reservationsResult] = await Promise.all([
      loadDiningAreas(context), tablesQuery,
      client.from('order_tables').select('order_id, order_group_id, table_id, joined_at, released_at').eq('tenant_id', context.tenantId).eq('venue_id', context.venueId).is('released_at', null),
      client.from('orders').select(orderColumns).eq('tenant_id', context.tenantId).eq('venue_id', context.venueId).in('status', ['open', 'carried_forward']),
      client.from('restaurant_order_equal_splits').select('order_group_id, paid_cents').eq('tenant_id', context.tenantId).eq('venue_id', context.venueId).eq('status', 'open'),
      client.from('reservation_tables').select('table_id, reservations!inner(id, customer_name, customer_phone, party_size, starts_at, ends_at, status)').eq('tenant_id', context.tenantId).eq('venue_id', context.venueId).in('reservations.status', ['confirmed', 'arrived', 'seated']).gte('reservations.starts_at', reservationRange.from).lt('reservations.starts_at', reservationRange.to).gt('reservations.ends_at', new Date().toISOString()),
    ])
    if (tablesResult.error) throw tablesResult.error
    if (linksResult.error) throw linksResult.error
    if (ordersResult.error) throw ordersResult.error
    if (equalSplitsResult.error) throw equalSplitsResult.error
    if (reservationsResult.error) throw reservationsResult.error
  }
  const tables = ((tablesResult.data ?? []) as TableRow[]).map(mapTable)
  const hasVirtualArea = tables.some((table) => table.areaId === `virtual:${context.venueId}`)
  const virtualArea: DiningArea | null = hasVirtualArea ? {
    id: `virtual:${context.venueId}`, tenantId: context.tenantId, venueId: context.venueId, name: 'Virtual',
    sortOrder: Number.MAX_SAFE_INTEGER, isActive: true, canvasWidth: 1200, canvasHeight: 800,
    mapElements: [], createdAt: '', updatedAt: '',
  } : null
  const areas = virtualArea ? [...loadedAreas, virtualArea] : loadedAreas
  const links = (linksResult.data ?? []) as OrderTableRow[]
  const orders = ((ordersResult.data ?? []) as OrderRow[]).map(mapOrder)
  let lines: RestaurantOrderLine[] = snapshot ? snapshot.lines.map(mapLine) : []
  if (orders.length && !snapshot) {
    const { data, error } = await client.from('order_lines').select(lineColumns).in('order_id', orders.map((order) => order.id))
    if (error) throw error
    lines = ((data ?? []) as OrderLineRow[]).map(mapLine)
  }
  const readyByLine = new Map<string, number>()
  if (!snapshot && hasTenantCapability(context, 'production') && lines.length) {
    const { data, error } = await client.from('production_line_allocations')
      .select('current_order_line_id, ready_quantity')
      .in('current_order_line_id', lines.map((line) => line.id))
    if (error) throw error
    ;((data ?? []) as Array<{ current_order_line_id: string; ready_quantity: number }>).forEach((allocation) => {
      readyByLine.set(allocation.current_order_line_id, (readyByLine.get(allocation.current_order_line_id) ?? 0) + Number(allocation.ready_quantity))
    })
  }
  for (const allocation of snapshot?.allocations ?? []) readyByLine.set(allocation.current_order_line_id, (readyByLine.get(allocation.current_order_line_id) ?? 0) + Number(allocation.ready_quantity))
  const orderByGroup = new Map<string, RestaurantOrder[]>()
  orders.forEach((order) => orderByGroup.set(order.orderGroupId, [...(orderByGroup.get(order.orderGroupId) ?? []), order]))
  orderByGroup.forEach((groupOrders) => groupOrders.sort((a, b) => a.splitSequence - b.splitSequence))
  const groupByOrder = new Map(orders.map((order) => [order.id, order.orderGroupId]))
  const totals = new Map<string, number>()
  const pendingUnits = new Map<string, number>()
  const readyUnits = new Map<string, number>()
  const paidCents = new Map<string, number>()
  ;((equalSplitsResult.data ?? []) as Array<{ order_group_id: string; paid_cents: number }>).forEach((split) => paidCents.set(split.order_group_id, (paidCents.get(split.order_group_id) ?? 0) + Number(split.paid_cents)))
  lines.forEach((line) => {
    const groupId = groupByOrder.get(line.orderId)
    if (!groupId) return
    totals.set(groupId, (totals.get(groupId) ?? 0) + quantityAmountCents(line.unitPriceCents, line.quantity))
    pendingUnits.set(groupId, (pendingUnits.get(groupId) ?? 0) + Math.max(0, line.quantity - line.servedQuantity))
    readyUnits.set(groupId, (readyUnits.get(groupId) ?? 0) + Math.max(0, (readyByLine.get(line.id) ?? 0) - line.servedQuantity))
  })
  const linkByTable = new Map(links.map((link) => [link.table_id, link]))
  const tableIdsByGroup = new Map<string, string[]>()
  links.forEach((link) => tableIdsByGroup.set(link.order_group_id, [...(tableIdsByGroup.get(link.order_group_id) ?? []), link.table_id]))
  const reservationsByTable = new Map<string, RestaurantTableReservation[]>()
  ;((reservationsResult.data ?? []) as unknown as ReservationMapRow[]).forEach((assignment) => {
    const value = Array.isArray(assignment.reservations) ? assignment.reservations[0] : assignment.reservations
    if (!value) return
    const reservation = { id: value.id, customerName: value.customer_name, customerPhone: value.customer_phone, partySize: Number(value.party_size), startsAt: value.starts_at, endsAt: value.ends_at, status: value.status }
    reservationsByTable.set(assignment.table_id, [...(reservationsByTable.get(assignment.table_id) ?? []), reservation])
  })
  reservationsByTable.forEach((items) => items.sort((first, second) => new Date(first.startsAt).getTime() - new Date(second.startsAt).getTime()))
  const mappedTables: RestaurantTableMapItem[] = tables.map((table) => {
    const link = linkByTable.get(table.id)
    const order = link ? orderByGroup.get(link.order_group_id)?.[0] : undefined
    const groupId = order?.orderGroupId
    // reservedUntil and reservationNote are legacy-only; persistent reservations are the source of truth.
    const tableReservations = reservationsByTable.get(table.id) ?? []
    return { ...table, status: order ? 'occupied' : 'free', orderId: order?.id ?? null, orderOpenedAt: order?.openedAt ?? null, guestCount: order?.guestCount ?? null, totalCents: groupId ? Math.max(0, (totals.get(groupId) ?? 0) - (paidCents.get(groupId) ?? 0)) : 0, pendingUnits: groupId ? (pendingUnits.get(groupId) ?? 0) : 0, readyUnits: groupId ? (readyUnits.get(groupId) ?? 0) : 0, groupTableIds: groupId ? (tableIdsByGroup.get(groupId) ?? []) : [], nextReservation: tableReservations[0] ?? null, reservationCount: tableReservations.length }
  })
  const assistSource = snapshot as (typeof snapshot & { assistConfiguration?: AssistConfiguration; observedAt?: string })
  const configuration = assistSource?.assistConfiguration
  if (configuration) saveCachedAssistConfiguration(context, configuration)
  const result: RestaurantMap = { areas, tables: mappedTables }
  if (configuration) {
    try {
      const scope = `${context.tenantId}:${context.venueId}`
      const observedAt = assistSource?.observedAt ?? new Date().toISOString()
      result.assist = configuration.tenantEnabled && configuration.venueEnabled
        ? buildAssistSnapshot(configuration, orders, lines, mappedTables, (snapshot?.allocations ?? []) as AssistAllocation[], observedAt, scope)
        : { configuration, orders: [], observedAt, contextKey: scope }
      const zoneNames = new Map(areas.map((area) => [area.id, area.name]))
      for (const order of result.assist.orders) order.zoneName = zoneNames.get(order.zoneId ?? '')
    } catch { /* Secondary projection failure must never prevent loading tables. */ }
  }
  return snapshot?.layout ? applyVenueLayout(result, snapshot.layout) : result
}

const orderReads = new Map<string, Promise<RestaurantOrderDetail>>()
export async function loadRestaurantOrder(context: TenantContext, orderId: string): Promise<RestaurantOrderDetail> {
  const key = [context.tenantId, context.venueId, context.userId, context.deviceId, orderId].join(':')
  const current = orderReads.get(key)
  if (current) return current
  const pending = fetchRestaurantOrder(context, orderId)
  orderReads.set(key, pending)
  try { return await pending } finally { if (orderReads.get(key) === pending) orderReads.delete(key) }
}

async function fetchRestaurantOrder(context: TenantContext, orderId: string): Promise<RestaurantOrderDetail> {
  const client = requireSupabase()
  const response = await client.rpc('pos_restaurant_order', { p_tenant_id: context.tenantId, p_venue_id: context.venueId, p_order_id: orderId })
  if (!response.error) {
    const value = response.data as { order: OrderRow; lines: OrderLineRow[]; tables: TableRow[]; registerName: string | null } | null
    if (!value) throw new Error('La comanda no está disponible en este local.')
    const lines = value.lines.map(mapLine)
    return { order: mapOrder(value.order), lines, tables: value.tables.map(mapTable), cashRegisterName: value.registerName ?? 'Caja', totalCents: lines.reduce((sum, line) => sum + quantityAmountCents(line.unitPriceCents, line.quantity), 0) }
  }
  if (!['PGRST202','42883'].includes(response.error.code)) throw response.error
  const orderResult = await client.from('orders').select(orderColumns).eq('tenant_id', context.tenantId).eq('venue_id', context.venueId).eq('id', orderId).single<OrderRow>()
  if (orderResult.error) throw orderResult.error
  const [linesResult, linksResult] = await Promise.all([
    client.from('order_lines').select(lineColumns).eq('tenant_id', context.tenantId).eq('order_id', orderId).order('created_at'),
    client.from('order_tables').select('table_id').eq('tenant_id', context.tenantId).eq('order_group_id', orderResult.data.order_group_id).is('released_at', null),
  ])
  if (linesResult.error) throw linesResult.error
  if (linksResult.error) throw linksResult.error
  const tableIds = (linksResult.data ?? []).map((row: { table_id: string }) => row.table_id)
  let tableRows: TableRow[] = []
  if (tableIds.length) {
    const { data, error } = await client.from('restaurant_tables').select(tableColumns).in('id', tableIds)
    if (error) throw error
    tableRows = (data ?? []) as TableRow[]
  }
  const lines = ((linesResult.data ?? []) as OrderLineRow[]).map(mapLine)
  const { data: register } = await client.from('cash_registers').select('name').eq('id', orderResult.data.cash_register_id).maybeSingle<{ name: string }>()
  return { order: mapOrder(orderResult.data), cashRegisterName: register?.name ?? 'Caja', lines, tables: tableRows.map(mapTable), totalCents: lines.reduce((sum, line) => sum + quantityAmountCents(line.unitPriceCents, line.quantity), 0) }
}

export async function createDiningArea(context: TenantContext, input: DiningAreaCreateInput) {
  const { data, error } = await requireSupabase().from('dining_areas').insert({ tenant_id: context.tenantId, venue_id: input.venueId, name: input.name.trim(), sort_order: input.sortOrder }).select(areaColumns).single<AreaRow>()
  if (error) throw error
  return mapArea(data)
}
export async function updateDiningArea(context: TenantContext, areaId: string, input: DiningAreaUpdateInput) {
  const { error } = await requireSupabase().from('dining_areas').update({ name: input.name?.trim(), sort_order: input.sortOrder, is_active: input.isActive, canvas_width: input.canvasWidth, canvas_height: input.canvasHeight, map_elements: input.mapElements }).eq('tenant_id', context.tenantId).eq('id', areaId)
  if (error) throw error
}
export async function deleteDiningArea(context: TenantContext, areaId: string) {
  const { error } = await requireSupabase().from('dining_areas').delete().eq('tenant_id', context.tenantId).eq('id', areaId)
  if (error?.code === '23503') throw new UserFacingError('No se puede eliminar la zona porque todavía contiene mesas. Elimina sus mesas primero.')
  if (error) throw error
}
export async function createRestaurantTable(context: TenantContext, input: RestaurantTableCreateInput) {
  const { data, error } = await requireSupabase().from('restaurant_tables').insert({ tenant_id: context.tenantId, venue_id: input.venueId, area_id: input.areaId, name: input.name.trim(), capacity: input.capacity, shape: input.shape, position_x: input.positionX, position_y: input.positionY, width: input.width, height: input.height, sort_order: input.sortOrder }).select(tableColumns).single<TableRow>()
  if (error) throw error
  return mapTable(data)
}
export async function createVirtualRestaurantTable(input: VirtualRestaurantTableCreateInput) {
  const { data, error } = await requireSupabase().rpc('create_virtual_restaurant_table', {
    p_cash_session_id: input.cashSessionId,
    p_device_id: input.deviceId,
    p_area_id: input.areaId,
    p_name: input.name.trim(),
    p_capacity: input.capacity,
    p_shape: input.shape,
  })
  if (error) throw error
  return String(data)
}
export async function saveQuickSaleAsVirtualTable(input: QuickSaleVirtualTableCreateInput): Promise<QuickSaleVirtualTableCreateResult> {
  const { data, error } = await requireSupabase().rpc('save_quick_sale_as_virtual_table', {
    p_cash_session_id: input.cashSessionId,
    p_device_id: input.deviceId,
    p_area_id: input.areaId,
    p_name: input.name.trim(),
    p_capacity: input.capacity,
    p_shape: input.shape,
    p_lines: buildCatalogOrderLinesPayload(input.lines),
    p_discount: input.discount,
  })
  if (error) throw error
  const result = data as Record<string, unknown>
  return { tableId: String(result.tableId), orderId: String(result.orderId), revision: Number(result.revision) }
}
export async function saveQuickSaleAsExistingTable(input: QuickSaleExistingTableSaveInput): Promise<QuickSaleExistingTableSaveResult> {
  const { data, error } = await requireSupabase().rpc('save_quick_sale_as_existing_table', {
    p_cash_session_id: input.cashSessionId,
    p_device_id: input.deviceId,
    p_table_id: input.tableId,
    p_lines: buildCatalogOrderLinesPayload(input.lines),
    p_discount: input.discount,
  })
  if (error) throw error
  const result = data as Record<string, unknown>
  return { tableId: String(result.tableId), orderId: String(result.orderId), revision: Number(result.revision) }
}
export async function deleteVirtualRestaurantTable(input: VirtualRestaurantTableDeleteInput) {
  const { error } = await requireSupabase().rpc('delete_session_virtual_restaurant_table', {
    p_cash_session_id: input.cashSessionId,
    p_device_id: input.deviceId,
    p_table_id: input.tableId,
  })
  if (!error) return
  if (error.message.includes('VIRTUAL_TABLE_HAS_PAYMENTS')) throw new UserFacingError('No se puede eliminar esta mesa temporal porque su comanda ya tiene cobros.')
  if (error.message.includes('VIRTUAL_TABLE_JOINED')) throw new UserFacingError('Separa la mesa temporal antes de eliminarla.')
  if (error.message.includes('VIRTUAL_TABLE_HAS_ACTIVE_RESERVATION')) throw new UserFacingError('No se puede eliminar esta mesa temporal porque tiene una reserva vigente.')
  if (error.message.includes('VIRTUAL_TABLE_SESSION_MISMATCH')) throw new UserFacingError('La mesa temporal pertenece a otro turno.')
  throw error
}
export async function updateRestaurantTable(context: TenantContext, tableId: string, input: RestaurantTableUpdateInput) {
  const { error } = await requireSupabase().from('restaurant_tables').update({ name: input.name?.trim(), capacity: input.capacity, shape: input.shape, position_x: input.positionX, position_y: input.positionY, width: input.width, height: input.height, is_active: input.isActive, sort_order: input.sortOrder }).eq('tenant_id', context.tenantId).eq('id', tableId)
  if (error) throw error
}
export async function deleteRestaurantTable(_context: TenantContext, tableId: string) {
  const { error } = await requireSupabase().rpc('delete_restaurant_table', { p_table_id: tableId })
  if (!error) return
  if (error.message.includes('TABLE_HAS_OPEN_ORDER')) throw new UserFacingError('No se puede eliminar la mesa mientras tenga una comanda abierta.')
  if (error.message.includes('TABLE_HAS_ACTIVE_RESERVATION')) throw new UserFacingError('No se puede eliminar la mesa mientras tenga una reserva vigente.')
  throw error
}

export async function openRestaurantOrder(input: OpenRestaurantOrderInput) { const { data, error } = await requireSupabase().rpc('open_restaurant_order', { p_table_ids: input.tableIds, p_guest_count: input.guestCount, p_cash_session_id: input.cashSessionId, p_device_id: input.deviceId }); if (error) throw error; return String(data) }
export async function setRestaurantOrderLineQuantity(lineId: string, quantity: number) { const { error } = await requireSupabase().rpc('set_restaurant_order_line_quantity', { p_line_id: lineId, p_quantity: quantity }); if (error) throw error }
export async function removeRestaurantOrderLine(lineId: string) { const { error } = await requireSupabase().rpc('remove_restaurant_order_line', { p_line_id: lineId }); if (error) throw error }
export async function removeRestaurantOrderLineConfirmed(lineId: string, expectedRevision: number) {
  const { data, error } = await requireSupabase().rpc('remove_restaurant_order_line_confirmed', {
    p_line_id: lineId,
    p_expected_revision: expectedRevision,
  })
  if (error) throw error
  return Number(data)
}

function mapEqualSplit(value: unknown): RestaurantEqualSplit {
  const row = value as Record<string, unknown>
  const read = (camel: string, snake: string) => row[camel] ?? row[snake]
  const totalCents = Number(read('totalCents', 'total_cents'))
  const partCount = Number(read('partCount', 'part_count'))
  const paidParts = Number(read('paidParts', 'paid_parts'))
  const paidCents = Number(read('paidCents', 'paid_cents'))
  const nextPartCents = paidParts >= partCount ? 0 : Math.floor(totalCents / partCount) + (paidParts + 1 <= totalCents % partCount ? 1 : 0)
  const explicitDefaultDiscount = read('nextDefaultDiscount', 'next_default_discount') as AppliedDiscount | null | undefined
  const storedDefault = read('defaultDiscount', 'default_discount') as Record<string, unknown> | null | undefined
  const calculationType = storedDefault?.calculationType as AppliedDiscount['calculationType'] | undefined
  const allocatedDefaultCents = calculationType
    ? Math.floor(Number(storedDefault?.amountCents ?? 0) / partCount) + (paidParts + 1 <= Number(storedDefault?.amountCents ?? 0) % partCount ? 1 : 0)
    : 0
  const fallbackDefaultDiscount: AppliedDiscount | null = storedDefault && calculationType
    ? {
        discountId: storedDefault.discountId ? String(storedDefault.discountId) : null,
        name: String(storedDefault.name ?? 'Descuento'),
        type: storedDefault.type as AppliedDiscount['type'],
        calculationType,
        value: calculationType === 'fixed' ? allocatedDefaultCents : Number(storedDefault.value),
        fixedApplication: storedDefault.fixedApplication === 'unit' || storedDefault.fixedApplication === 'line'
          ? 'unit'
          : 'ticket',
        roundingIncrementCents: (storedDefault.roundingIncrementCents as AppliedDiscount['roundingIncrementCents']) ?? null,
        color: storedDefault.color ? String(storedDefault.color) : null,
      }
    : null
  const nextDefaultDiscount = explicitDefaultDiscount ?? fallbackDefaultDiscount
  const fallbackDiscountAmount = nextDefaultDiscount ? Math.min(nextPartCents, allocatedDefaultCents) : 0
  const nextDefaultDiscountAmountCents = Number(read('nextDefaultDiscountAmountCents', 'next_default_discount_amount_cents') ?? fallbackDiscountAmount)
  return {
    id: String(row.id), orderId: String(read('orderId', 'order_id')), orderGroupId: String(read('orderGroupId', 'order_group_id')),
    totalCents, partCount, paidParts, paidCents,
    singleTicket: Boolean(read('singleTicket', 'single_ticket')),
    tenders: ((row.tenders ?? []) as Array<Record<string, unknown>>).map((tender) => ({
      id: String(tender.id), partNumber: Number(tender.part_number), method: tender.method as PaymentMethod | null,
      amountCents: Number(tender.amount_cents), discountAmountCents: Number(tender.discount_amount_cents),
      receivedCents: tender.received_cents === null ? null : Number(tender.received_cents), changeCents: Number(tender.change_cents),
    })),
    remainingParts: Number(read('remainingParts', 'remaining_parts') ?? partCount - paidParts),
    remainingCents: Number(read('remainingCents', 'remaining_cents') ?? totalCents - paidCents),
    nextPartCents: Number(read('nextPartCents', 'next_part_cents') ?? nextPartCents),
    nextDefaultDiscount,
    nextDefaultDiscountAmountCents,
    nextDefaultTotalCents: Number(read('nextDefaultTotalCents', 'next_default_total_cents') ?? Math.max(0, nextPartCents - nextDefaultDiscountAmountCents)),
    status: row.status as RestaurantEqualSplit['status'], revision: Number(row.revision),
    allowPendingService: Boolean(read('allowPendingService', 'allow_pending_service')),
  }
}

export async function loadRestaurantOrderGroup(context: TenantContext, orderId: string): Promise<RestaurantOrderGroupDetail> {
  const client = requireSupabase()
  const { data: selected, error: selectedError } = await client.from('orders').select(orderColumns)
    .eq('tenant_id', context.tenantId).eq('venue_id', context.venueId).eq('id', orderId).single<OrderRow>()
  if (selectedError) throw selectedError
  const [ordersResult, linksResult, registerResult] = await Promise.all([
    client.from('orders').select(orderColumns).eq('tenant_id', context.tenantId).eq('venue_id', context.venueId)
      .eq('order_group_id', selected.order_group_id).neq('status', 'cancelled').order('split_sequence'),
    client.from('order_tables').select('table_id').eq('tenant_id', context.tenantId)
      .eq('order_group_id', selected.order_group_id).is('released_at', null),
    client.from('cash_registers').select('name').eq('id', selected.cash_register_id).maybeSingle<{ name: string }>(),
  ])
  if (ordersResult.error) throw ordersResult.error
  if (linksResult.error) throw linksResult.error
  const orderRows = (ordersResult.data ?? []) as OrderRow[]
  const orderIds = orderRows.map((order) => order.id)
  const tableIds = (linksResult.data ?? []).map((row: { table_id: string }) => row.table_id)
  const [linesResult, tablesResult] = await Promise.all([
    orderIds.length ? client.from('order_lines').select(lineColumns).in('order_id', orderIds).order('created_at') : Promise.resolve({ data: [], error: null }),
    tableIds.length ? client.from('restaurant_tables').select(tableColumns).in('id', tableIds) : Promise.resolve({ data: [], error: null }),
  ])
  if (linesResult.error) throw linesResult.error
  if (tablesResult.error) throw tablesResult.error
  const allLines = ((linesResult.data ?? []) as OrderLineRow[]).map(mapLine)
  const tables = ((tablesResult.data ?? []) as TableRow[]).map(mapTable)
  const orders = orderRows.map((row) => {
    const lines = allLines.filter((line) => line.orderId === row.id)
    return { order: mapOrder(row), cashRegisterName: registerResult.data?.name ?? 'Caja', lines, tables, totalCents: lines.reduce((sum, line) => sum + quantityAmountCents(line.unitPriceCents, line.quantity), 0) }
  })
  return { id: selected.order_group_id, orders, tables }
}
export async function moveRestaurantOrder(orderId: string, tableId: string) { const { error } = await requireSupabase().rpc('move_restaurant_order', { p_order_id: orderId, p_target_table_id: tableId }); if (error) throw error }
export async function moveRestaurantOrderLines(sourceOrderId: string, targetOrderId: string | null, expectedSourceRevision: number, expectedTargetRevision: number | null, moves: RestaurantOrderLineMove[]) {
  const { data, error } = await requireSupabase().rpc('move_restaurant_order_lines', {
    p_source_order_id: sourceOrderId,
    p_target_order_id: targetOrderId,
    p_expected_source_revision: expectedSourceRevision,
    p_expected_target_revision: expectedTargetRevision,
    p_moves: moves.map((move) => ({ lineId: move.lineId, quantity: move.quantity })),
  })
  if (error) throw error
  return data as MoveRestaurantOrderLinesResult
}
export async function closeRestaurantOrder(orderId: string, paymentMethod: PaymentMethod | null, receivedCents: number | null, allowPending = false, discount: AppliedDiscount | null = null, invoiceCustomer: Customer | null = null, cashlogyTransaction: CashlogyTransaction | null = null) {
  const paymentParams = {
    p_order_id: orderId,
    p_payment_method: paymentMethod,
    p_received_cents: receivedCents,
    p_allow_pending: allowPending,
    p_discount: discount,
  }
  const { data, error } = cashlogyTransaction
    ? await requireSupabase().rpc('close_restaurant_order_cashlogy', {
      ...paymentParams,
      p_cashlogy_request_id: cashlogyTransaction.requestId,
      p_cashlogy_transaction_id: cashlogyTransaction.id,
      p_customer_id: invoiceCustomer?.id ?? null,
    })
    : invoiceCustomer
      ? await requireSupabase().rpc('close_restaurant_order_with_invoice', {
        ...paymentParams,
        p_customer_id: invoiceCustomer.id,
      })
      : await requireSupabase().rpc('close_restaurant_order_checked_v2', paymentParams)
  if (error) throw error
  return data as CloseRestaurantOrderResult
}

export async function payRestaurantIdempotent(context: TenantContext, operationId: string, action: 'close' | 'equal_part' | 'selected_items', params: Record<string, unknown>) {
  const { data, error } = await requireSupabase().rpc(params.singleTicket ? 'pay_restaurant_equal_split_single_idempotent' : 'pay_restaurant_idempotent', {
    p_operation_id: operationId, p_tenant_id: context.tenantId, p_venue_id: context.venueId,
    p_device_id: context.deviceId, p_action: action, p_params: params,
  })
  if (error) throw error
  const result = data as Record<string, unknown>
  return action === 'equal_part' && result.split
    ? { ...result, split: mapEqualSplit(result.split) }
    : result
}

export async function cancelEmptyRestaurantOrder(orderId: string, expectedRevision: number) {
  const { data, error } = await requireSupabase().rpc('cancel_empty_restaurant_order', {
    p_order_id: orderId,
    p_expected_revision: expectedRevision,
  })
  if (error) throw error
  return Number(data)
}

export async function cleanupVirtualRoomRestaurantTable(input: {
  cashSessionId: string
  deviceId: string
  tableId: string
  closeAsPaid: boolean
}) {
  const { data, error } = await requireSupabase().rpc('cleanup_virtual_room_restaurant_table', {
    p_cash_session_id: input.cashSessionId,
    p_device_id: input.deviceId,
    p_table_id: input.tableId,
    p_close_as_paid: input.closeAsPaid,
  })
  if (error) throw error
  return Boolean(data)
}

export async function loadRestaurantEqualSplit(context: TenantContext, orderId: string): Promise<RestaurantEqualSplit | null> {
  const { data, error } = await requireSupabase().from('restaurant_order_equal_splits').select('*, tenders:restaurant_equal_split_tenders(*)')
    .eq('tenant_id', context.tenantId).eq('venue_id', context.venueId).eq('order_id', orderId).eq('status', 'open').maybeSingle()
  if (error) throw error
  return data ? mapEqualSplit(data) : null
}

export async function configureRestaurantEqualSplit(orderId: string, partCount: number, expectedRevision: number, defaultDiscount: AppliedDiscount | null) {
  const { data, error } = await requireSupabase().rpc('configure_restaurant_equal_split_single', { p_order_id: orderId, p_part_count: partCount, p_expected_order_revision: expectedRevision, p_default_discount: defaultDiscount })
  if (error) throw error
  return mapEqualSplit(data)
}

export async function payRestaurantEqualPart(splitId: string, paymentMethod: PaymentMethod | null, receivedCents: number | null, allowPending: boolean, discount: AppliedDiscount | null, useDefaultDiscount: boolean, cashlogyTransaction: CashlogyTransaction | null = null): Promise<PayRestaurantEqualPartResult> {
  const paymentParams = {
    p_split_id: splitId,
    p_payment_method: paymentMethod,
    p_received_cents: receivedCents,
    p_allow_pending: allowPending,
    p_discount: discount,
    p_use_default_discount: useDefaultDiscount,
  }
  const { data, error } = cashlogyTransaction
    ? await requireSupabase().rpc('pay_restaurant_order_equal_part_cashlogy', {
      ...paymentParams,
      p_cashlogy_request_id: cashlogyTransaction.requestId,
      p_cashlogy_transaction_id: cashlogyTransaction.id,
    })
    : await requireSupabase().rpc('pay_restaurant_order_equal_part', paymentParams)
  if (error) throw error
  const result = data as Record<string, unknown>
  return { ...result, requiresConfirmation: Boolean(result.requiresConfirmation), pendingUnits: Number(result.pendingUnits), split: mapEqualSplit(result.split) } as PayRestaurantEqualPartResult
}

export async function payRestaurantOrderItems(orderId: string, expectedRevision: number, moves: RestaurantOrderLineMove[], paymentMethod: PaymentMethod | null, receivedCents: number | null, allowPending: boolean, discount: AppliedDiscount | null, cashlogyTransaction: CashlogyTransaction | null = null): Promise<PayRestaurantOrderItemsResult> {
  const paymentParams = {
    p_order_id: orderId,
    p_expected_revision: expectedRevision,
    p_items: moves.map((move) => ({ lineId: move.lineId, quantity: move.quantity })),
    p_payment_method: paymentMethod,
    p_received_cents: receivedCents,
    p_allow_pending: allowPending,
    p_discount: discount,
  }
  const { data, error } = cashlogyTransaction
    ? await requireSupabase().rpc('pay_restaurant_order_items_cashlogy', {
      ...paymentParams,
      p_cashlogy_request_id: cashlogyTransaction.requestId,
      p_cashlogy_transaction_id: cashlogyTransaction.id,
    })
    : await requireSupabase().rpc('pay_restaurant_order_items', paymentParams)
  if (error) throw error
  const result = data as Record<string, unknown>
  return { ...result, requiresConfirmation: Boolean(result.requiresConfirmation), pendingUnits: Number(result.pendingUnits) } as PayRestaurantOrderItemsResult
}

/** Atomic cloud sale plus locally prepared fiscal record for a production installation. */
export async function payRestaurantLocalFiscal(
  action: 'close' | 'equal_part' | 'selected_items',
  params: Record<string, unknown>, record: Record<string, unknown>, invoice: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const { data, error } = await requireSupabase().rpc(params.singleTicket ? 'pay_restaurant_equal_split_single_fiscal' : 'pay_restaurant_local_fiscal', {
    p_action: action, p_params: params, p_record: record, p_invoice: invoice,
  })
  if (error) throw error
  const result = data as Record<string, unknown>
  return action === 'equal_part' && result.split
    ? { ...result, split: mapEqualSplit(result.split) }
    : result
}
export async function saveRestaurantOrderLines(detail: RestaurantOrderDetail): Promise<SaveRestaurantOrderLinesResult> {
  const { data, error } = await requireSupabase().rpc('save_catalog_order_lines', {
    p_order_id: detail.order.id,
    p_expected_revision: detail.order.revision,
    p_lines: buildRestaurantOrderLinesPayload(detail),
  })
  if (error) throw error
  return data as SaveRestaurantOrderLinesResult
}

export async function markRestaurantOrderLineUnitsServed(lineId: string, units = 1) {
  const { error } = await requireSupabase().rpc('mark_order_line_units_served', { p_order_line_id: lineId, p_units: units })
  if (error) throw error
}

export async function markRestaurantOrderLineFullyServed(lineId: string) {
  const { error } = await requireSupabase().rpc('mark_order_line_fully_served', { p_order_line_id: lineId })
  if (error) throw error
}

export async function markRestaurantOrderFullyServed(orderId: string) {
  const { error } = await requireSupabase().rpc('mark_order_fully_served', { p_order_id: orderId })
  if (error) throw error
}

export async function loadRestaurantOrderPendingUnits(context: TenantContext, orderId: string) {
  const detail = await loadRestaurantOrder(context, orderId)
  return { detail, pendingUnits: getOrderPendingUnits(detail.lines) }
}

export async function loadOpenRestaurantOrders(context: TenantContext, cashSessionId?: string) {
  let query = requireSupabase().from('orders').select(orderColumns).eq('tenant_id', context.tenantId).eq('venue_id', context.venueId).eq('status', 'open').order('opened_at')
  if (cashSessionId) query = query.eq('cash_session_id', cashSessionId)
  const { data, error } = await query
  if (error) throw error
  return ((data ?? []) as OrderRow[]).map(mapOrder)
}

export function subscribeToRestaurantMap(
  context: TenantContext,
  onChange: () => void,
  onStatus?: (status: string, error?: Error) => void,
) {
  if (!supabase) return () => undefined
  const channel = supabase.channel(`restaurant-map:${context.tenantId}:${context.venueId}`)
  channel.on('postgres_changes', { event: '*', schema: 'public', table: 'venues', filter: `id=eq.${context.venueId}` }, () => {
    venueConfig.clear()
    onChange()
  })
  ;(['dining_areas', 'order_groups', 'orders', 'order_tables', 'order_lines', 'restaurant_tables', 'restaurant_order_equal_splits', 'restaurant_order_equal_split_payments', 'reservations', 'reservation_tables', 'production_line_allocations'] as const).forEach((table) => channel.on('postgres_changes', { event: '*', schema: 'public', table, filter: `venue_id=eq.${context.venueId}` }, onChange))
  channel.subscribe((status, error) => onStatus?.(status, error))
  return () => { void supabase?.removeChannel(channel) }
}
